import Phaser from "phaser";
import {
  ANIMATION_FRAME_RATES,
  ANIMATION_KEYS,
  ASSET_KEYS,
  ASSET_PATHS,
  DIRECTIONS,
  GAME_CONFIG,
  GAME_MODES,
  GHOST_HOUSE,
  GHOST_SCATTER_TARGETS,
  INITIAL_GAME_STATE,
  PACMAN_START_POSITION,
  RENDER_DEPTHS,
  SOUND_KEYS,
  SPRITE_DIMENSIONS,
  TIMING,
} from "../constants/game";
import {
  GameSceneData,
  GameState,
  Ghost,
  Intersection,
  NumberPill,
  PacmanGame as IPacmanGame,
  Point,
  QuestionData,
  RunningGameState,
} from "../types/game";
import { SoundManager } from "./SoundManager";
import {
  BoardManager,
  GhostAI,
  MovementManager,
  PillManager,
  CollisionManager,
} from "./managers";

/**
 * Refactored PacmanGame Scene
 * Uses specialized manager classes for better organization and maintainability
 */
export class PacmanGame extends Phaser.Scene implements IPacmanGame {
  // Game state management
  gameState: GameState = GameState.MENU;
  lives: number = INITIAL_GAME_STATE.LIVES;
  isPacmanAlive: boolean = INITIAL_GAME_STATE.IS_PACMAN_ALIVE;

  // Phaser objects
  pacman!: Phaser.Physics.Arcade.Sprite;
  map!: Phaser.Tilemaps.Tilemap;
  dots!: Phaser.Physics.Arcade.Group;
  powerPills!: Phaser.Physics.Arcade.Group;
  pills!: Phaser.Physics.Arcade.Group;
  cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  pinkGhost!: Ghost;
  orangeGhost!: Ghost;
  redGhost!: Ghost;
  blueGhost!: Ghost;
  ghosts: Ghost[] = [];

  // Core game properties (needed for manager coordination)
  blockSize: number = GAME_CONFIG.BLOCK_SIZE;
  speed: number = GAME_CONFIG.SPEED;

  // Properties required by interface (delegated to managers)
  scatterModeDuration: number = 7000;
  chaseModeDuration: number = 20000;
  scaredModeDuration: number = 9000;
  modeTimer: ReturnType<typeof setTimeout> | null = null;
  oldNextIntersection: Point | null = null;

  // Callbacks passed from App.tsx
  increaseSmallDotsCount?: (count: number) => void;
  increasePowerPillsCount?: (count: number) => void;
  increaseCorrectAnswersCount?: (count: number) => void;

  onGameOver?: () => void;
  onLifeLost?: (livesRemaining: number) => void;
  onGameStateChange?: (state: GameState) => void;
  setIsGameAssetsLoaded?: (isGameAssetsLoaded: boolean) => void;
  setAssetLoadingProgress?: (assetLoadingProgress: number) => void;
  onPillEaten?: (answer: number, correctAnswer: number) => void;
  setRunningGameState?: (runningGameState: RunningGameState) => void;
  onGamePaused?: () => void;

  // Manager instances
  private soundManager!: SoundManager;
  private boardManager!: BoardManager;
  private ghostAI!: GhostAI;
  private movementManager!: MovementManager;
  private pillManager!: PillManager;
  private collisionManager!: CollisionManager;

  // Store callback for PillManager (set in init, used in create)
  private pillManagerCallback?: () => QuestionData | null;

  // Getter properties that delegate to managers (for interface compatibility)
  get direction(): string {
    return this.movementManager?.getDirection() || INITIAL_GAME_STATE.DIRECTION;
  }

  get previousDirection(): string {
    return (
      this.movementManager?.getPreviousDirection() ||
      INITIAL_GAME_STATE.PREVIOUS_DIRECTION
    );
  }

  get board(): number[][] {
    return this.boardManager?.getBoard() || [];
  }

  get ghostSpeed(): number {
    return this.speed * GAME_CONFIG.GHOST_SPEED_MULTIPLIER;
  }

  get intersections(): Intersection[] {
    return this.boardManager?.getIntersections() || [];
  }

  get nextIntersection(): Point | null {
    return this.movementManager?.getNextIntersection() || null;
  }

  get currentMode(): string {
    return this.ghostAI?.getCurrentMode() || INITIAL_GAME_STATE.CURRENT_MODE;
  }

  get previouseMode(): string | undefined {
    return this.currentMode === INITIAL_GAME_STATE.CURRENT_MODE
      ? undefined
      : this.currentMode;
  }

  get hasRespawned(): boolean {
    return false; // Managed internally by MovementManager
  }

  // Override correctAnswer to delegate to PillManager
  set correctAnswer(_value: number | undefined) {
    // No-op setter for interface compatibility
  }

  get correctAnswer(): number | undefined {
    return this.pillManager?.getCorrectAnswer();
  }

  constructor() {
    super({ key: "PacmanGame" });
  }

  /**
   * Initialize method called by Phaser when scene starts
   * Gets the typed callbacks from the registry
   */
  init(): void {
    // Get the typed callbacks from the registry
    const gameCallbacks: GameSceneData =
      this.registry.get("gameCallbacks") || {};

    // Store the passed functions with proper typing
    this.increaseSmallDotsCount = gameCallbacks.increaseSmallDotsCount;
    this.increasePowerPillsCount = gameCallbacks.increasePowerPillsCount;
    this.increaseCorrectAnswersCount =
      gameCallbacks.increaseCorrectAnswersCount;
    this.onGameOver = gameCallbacks.onGameOver;
    this.onLifeLost = gameCallbacks.onLifeLost;
    this.onGameStateChange = gameCallbacks.onGameStateChange;
    this.setIsGameAssetsLoaded = gameCallbacks.setIsGameAssetsLoaded;
    this.setAssetLoadingProgress = gameCallbacks.setAssetLoadingProgress;
    this.onPillEaten = gameCallbacks.onPillEaten;
    this.setRunningGameState = gameCallbacks.setRunningGameState;
    this.onGamePaused = gameCallbacks.onGamePaused;
    // this.onWrongAnswer = gameCallbacks.onWrongAnswer;

    // Set up PillManager callback if available (pillManager will be created in create())
    // We'll set it up again in create() to ensure it's set, but this handles early calls
    this.pillManagerCallback = gameCallbacks.requestQuestion;

    console.log("PacmanGame initialized with functions:", {
      hasIncreaseSmallDotsCount: !!this.increaseSmallDotsCount,
      hasIncreasePowerPillsCount: !!this.increasePowerPillsCount,
      hasIncreaseCorrectAnswersCount: !!this.increaseCorrectAnswersCount,
      hasOnGameOver: !!this.onGameOver,
      hasOnLifeLost: !!this.onLifeLost,
      hasOnGameStateChange: !!this.onGameStateChange,
      hasSetIsGameAssetsLoaded: !!this.setIsGameAssetsLoaded,
      hasRequestQuestion: !!gameCallbacks.requestQuestion,
    });

    // Initialize sound manager (sounds will be initialized after loading)
    this.soundManager = new SoundManager(this);

    // Initialize game state
    this.setGameState(GameState.MENU);
  }

  /**
   * Preload game assets
   */
  preload(): void {
    // Update loading bar on progress
    this.load.on("progress", (value: number) => {
      if (this.setAssetLoadingProgress) {
        this.setAssetLoadingProgress(value * 100);
      }
    });

    // Load all game assets
    this.loadGameAssets();
    this.loadSoundAssets();
  }

  /**
   * Create the game scene - Initialize all managers and set up the game
   */
  create(): void {
    // Set up tilemap
    this.map = this.make.tilemap({ key: "map" });
    const tileset = this.map.addTilesetImage("pacman tileset");
    if (!tileset) return;

    const layer = this.map.createLayer("Tile Layer 1", [tileset]);
    if (!layer) return;
    layer.setCollisionByExclusion([-1], true);

    // Scale tilemap layer to match new block size (3x scale: 16 -> 48)
    const scaleFactor = GAME_CONFIG.BLOCK_SIZE / 16; // 48 / 16 = 3
    layer.setScale(scaleFactor);

    // Set physics world bounds to match game config dimensions
    this.physics.world.setBounds(0, 0, GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT);

    // Create Pacman sprite (using pacman1 as base since pacman was removed)
    this.pacman = this.physics.add.sprite(
      PACMAN_START_POSITION.X,
      PACMAN_START_POSITION.Y,
      "pacman1"
    );
    this.pacman.setDepth(RENDER_DEPTHS.PACMAN);
    this.pacman.setDisplaySize(
      SPRITE_DIMENSIONS.FRAME_WIDTH,
      SPRITE_DIMENSIONS.FRAME_HEIGHT
    );

    // Create Pacman animations
    this.anims.create({
      key: ANIMATION_KEYS.PACMAN_ANIM,
      frames: [
        { key: ASSET_KEYS.PACMAN },
        { key: ASSET_KEYS.PACMAN1 },
        { key: ASSET_KEYS.PACMAN2 },
        { key: ASSET_KEYS.PACMAN3 },
        { key: ASSET_KEYS.PACMAN4 },
        { key: ASSET_KEYS.PACMAN5 },
      ],
      frameRate: ANIMATION_FRAME_RATES.PACMAN,
      repeat: -1,
    });
    this.pacman.play(ANIMATION_KEYS.PACMAN_ANIM);

    // Create ghost animations
    // Pink Ghost
    this.anims.create({
      key: ANIMATION_KEYS.PINK_GHOST_ANIM,
      frames: [{ key: ASSET_KEYS.PINK_GHOST }, { key: ASSET_KEYS.PINK_GHOST1 }],
      frameRate: ANIMATION_FRAME_RATES.GHOST,
      repeat: -1,
    });

    // Orange Ghost
    this.anims.create({
      key: ANIMATION_KEYS.ORANGE_GHOST_ANIM,
      frames: [
        { key: ASSET_KEYS.ORANGE_GHOST },
        { key: ASSET_KEYS.ORANGE_GHOST1 },
      ],
      frameRate: ANIMATION_FRAME_RATES.GHOST,
      repeat: -1,
    });

    // Red Ghost
    this.anims.create({
      key: ANIMATION_KEYS.RED_GHOST_ANIM,
      frames: [{ key: ASSET_KEYS.RED_GHOST }, { key: ASSET_KEYS.RED_GHOST1 }],
      frameRate: ANIMATION_FRAME_RATES.GHOST,
      repeat: -1,
    });

    // Blue Ghost
    this.anims.create({
      key: ANIMATION_KEYS.BLUE_GHOST_ANIM,
      frames: [{ key: ASSET_KEYS.BLUE_GHOST }, { key: ASSET_KEYS.BLUE_GHOST1 }],
      frameRate: ANIMATION_FRAME_RATES.GHOST,
      repeat: -1,
    });

    this.anims.create({
      key: ANIMATION_KEYS.PACMAN_DEATH,
      frames: [
        { key: ASSET_KEYS.PACMAN_DEATH1 },
        { key: ASSET_KEYS.PACMAN_DEATH2 },
        { key: ASSET_KEYS.PACMAN_DEATH3 },
        { key: ASSET_KEYS.PACMAN_DEATH4 },
        { key: ASSET_KEYS.PACMAN_DEATH5 },
        { key: ASSET_KEYS.PACMAN_DEATH6 },
        { key: ASSET_KEYS.PACMAN_DEATH7 },
        { key: ASSET_KEYS.PACMAN_DEATH8 },
        { key: ASSET_KEYS.PACMAN_DEATH9 },
        { key: ASSET_KEYS.PACMAN_DEATH10 },
        { key: ASSET_KEYS.PACMAN_DEATH11 },
        { key: ASSET_KEYS.PACMAN_DEATH12 },
      ],
      frameRate: ANIMATION_FRAME_RATES.PACMAN_DEATH,
      repeat: 0,
    });

    this.physics.add.collider(this.pacman, layer);

    // Create physics groups
    this.dots = this.physics.add.group();
    this.powerPills = this.physics.add.group();
    this.pills = this.physics.add.group();

    // ========== Initialize Managers ==========

    // 1. Board Manager - Handles board, dots, power pills, intersections
    this.boardManager = new BoardManager(
      this,
      this.map,
      this.blockSize,
      this.dots,
      this.powerPills
    );
    this.boardManager.populateBoardAndTrackEmptyTiles(layer);
    this.boardManager.detectIntersections();

    // 2. Pill Manager - Handles number pills
    this.pillManager = new PillManager(
      this,
      this.map,
      this.blockSize,
      this.dots,
      this.pills
    );
    this.pillManager.computeValidPillLocations();

    // Set up PillManager callback for requesting questions from React
    // Try to get from registry first (in case it was set after init)
    const gameCallbacks: GameSceneData =
      this.registry.get("gameCallbacks") || {};
    const requestQuestionCallback =
      gameCallbacks.requestQuestion || this.pillManagerCallback;

    console.log(
      "Setting up PillManager callback, requestQuestion available:",
      !!requestQuestionCallback
    );
    if (requestQuestionCallback) {
      this.pillManager.setRequestQuestionCallback(requestQuestionCallback);
      console.log("PillManager callback set successfully");
    } else {
      console.warn(
        "requestQuestion callback not found in gameCallbacks or pillManagerCallback"
      );
    }

    // Set up cross-manager reference: BoardManager needs pill locations
    this.boardManager.setPillLocationsCallback(() =>
      this.pillManager.getCurrentPillLocations()
    );

    // Set up cross-manager reference: PillManager needs Pacman position and direction
    this.pillManager.setPacmanPositionCallback(() => {
      if (this.pacman && this.pacman.active) {
        return { x: this.pacman.x, y: this.pacman.y };
      }
      return null;
    });
    this.pillManager.setPacmanDirectionCallback(() => {
      if (this.movementManager) {
        return this.movementManager.getDirection();
      }
      return null;
    });

    // 3. Ghost AI - Handles ghost behavior and movement
    this.ghostAI = new GhostAI(
      this,
      this.blockSize,
      this.speed,
      INITIAL_GAME_STATE.DIRECTION
    );

    // Initialize ghosts
    this.ghosts = this.ghostAI.initializeAllGhosts(layer);
    [this.pinkGhost, this.redGhost, this.orangeGhost, this.blueGhost] =
      this.ghosts;

    // Update ghost animations (will stop if in house, start if outside)
    // this.ghostAI.updateAllGhostAnimations();

    // Set up ghost AI references
    this.ghostAI.setReferences(
      this.pacman,
      this.ghosts,
      () => this.boardManager.getIntersections(),
      (start, target) => this.boardManager.aStarAlgorithm(start, target),
      this.soundManager
    );

    // Initialize ghost paths
    const startPoint = { x: GHOST_HOUSE.ENTRANCE_X, y: GHOST_HOUSE.ENTRANCE_Y };
    this.pinkGhost.path = this.boardManager.aStarAlgorithm(
      startPoint,
      GHOST_SCATTER_TARGETS.PINKY
    );
    this.pinkGhost.nextIntersection = this.pinkGhost.path.shift() || null;

    this.blueGhost.path = this.boardManager.aStarAlgorithm(
      startPoint,
      GHOST_SCATTER_TARGETS.INKY
    );
    this.blueGhost.nextIntersection = this.blueGhost.path.shift() || null;

    this.orangeGhost.path = this.boardManager.aStarAlgorithm(
      startPoint,
      GHOST_SCATTER_TARGETS.CLYDE
    );
    this.orangeGhost.nextIntersection = this.orangeGhost.path.shift() || null;

    this.redGhost.path = this.boardManager.aStarAlgorithm(
      startPoint,
      GHOST_SCATTER_TARGETS.BLINKY
    );
    this.redGhost.nextIntersection = this.redGhost.path.shift() || null;

    // 4. Movement Manager - Handles Pacman movement
    const cursors = this.input.keyboard?.createCursorKeys();
    if (!cursors) {
      console.error("Failed to initialize keyboard cursors");
      return;
    }
    this.cursors = cursors;

    this.movementManager = new MovementManager(
      this,
      this.pacman,
      this.blockSize,
      this.speed,
      INITIAL_GAME_STATE.DIRECTION,
      INITIAL_GAME_STATE.PREVIOUS_DIRECTION
    );
    this.movementManager.setReferences(this.cursors, () =>
      this.boardManager.getIntersections()
    );

    // 5. Collision Manager - Handles all collision callbacks
    this.collisionManager = new CollisionManager(this);
    this.collisionManager.setCallbacks({
      increaseSmallDotsCount: this.increaseSmallDotsCount,
      increasePowerPillsCount: this.increasePowerPillsCount,
      increaseCorrectAnswersCount: this.increaseCorrectAnswersCount,
      onPillEaten: this.onPillEaten,
      // onWrongAnswer: this.onWrongAnswer,
      pacmanDiesCallback: () => this.pacmanDies(),
      getCorrectAnswer: () => this.pillManager.getCorrectAnswer(),
      getCurrentMode: () => this.ghostAI.getCurrentMode(),
      respawnGhostCallback: (ghost) => this.respawnGhost(ghost),
      soundManager: this.soundManager,
    });

    // ========== Set Up Collision Detection ==========
    this.physics.add.overlap(
      this.pacman,
      this.dots,
      (pacman, dot) => {
        const pacmanSprite = pacman as Phaser.Physics.Arcade.Sprite;
        const dotSprite = dot as Phaser.Physics.Arcade.Sprite;
        this.collisionManager.eatDot(pacmanSprite, dotSprite);
      },
      undefined,
      this
    );

    this.physics.add.overlap(
      this.pacman,
      this.powerPills,
      (pacman, powerPill) => {
        const pacmanSprite = pacman as Phaser.Physics.Arcade.Sprite;
        const powerPillSprite = powerPill as Phaser.Physics.Arcade.Sprite;
        this.collisionManager.eatPowerPill(pacmanSprite, powerPillSprite, () =>
          this.setGhostsToScaredMode()
        );
      },
      undefined,
      this
    );

    this.physics.add.overlap(
      this.pacman,
      this.pills,
      (pacman, pill) => {
        const pacmanSprite = pacman as Phaser.Physics.Arcade.Sprite;
        const pillSprite = pill as NumberPill;
        this.collisionManager.eatPill(pacmanSprite, pillSprite);
      },
      undefined,
      this
    );

    this.ghosts.forEach((ghost) => {
      this.physics.add.overlap(
        this.pacman,
        ghost,
        (pacman, ghostSprite) => {
          const pacmanSprite = pacman as Phaser.Physics.Arcade.Sprite;
          const ghost = ghostSprite as Ghost;
          this.collisionManager.handlePacmanGhostCollision(pacmanSprite, ghost);
        },
        undefined,
        this
      );
    });

    // Initialize sounds
    this.soundManager.initializeSounds();

    console.log("✅ All managers initialized successfully!");

    // Notify that game assets are loaded and ready
    if (this.setIsGameAssetsLoaded) {
      this.setIsGameAssetsLoaded(true);
    }
  }

  /**
   * Set ghosts to scared mode (called by CollisionManager)
   */
  private setGhostsToScaredMode(): void {
    this.ghostAI.setGhostsToScaredMode();
  }

  /**
   * Update loop - Called every frame
   */
  update(): void {
    if (!this.isPacmanAlive || this.lives === 0) return;

    // Stop coffee-break music
    // this.soundManager.stopBackgroundMusic();

    // Handle Pacman input and movement
    this.movementManager.handleDirectionInput();
    this.movementManager.handlePacmanMovement();
    this.movementManager.teleportPacmanAcrossWorldBounds();

    // Update ghost AI with current Pacman direction
    this.ghostAI.updatePacmanDirection(this.movementManager.getDirection());

    // Handle ghost movement (order matters: direction first, then movement)
    if (this.pinkGhost.enteredMaze) {
      this.ghostAI.handleGhostDirection(this.pinkGhost);
      this.ghostAI.handleGhostMovement(this.pinkGhost);
    }
    if (this.orangeGhost.enteredMaze) {
      this.ghostAI.handleGhostDirection(this.orangeGhost);
      this.ghostAI.handleGhostMovement(this.orangeGhost);
    }
    if (this.blueGhost.enteredMaze) {
      this.ghostAI.handleGhostDirection(this.blueGhost);
      this.ghostAI.handleGhostMovement(this.blueGhost);
    }
    if (this.redGhost.enteredMaze) {
      this.ghostAI.handleGhostDirection(this.redGhost);
      this.ghostAI.handleGhostMovement(this.redGhost);
    }
  }

  /**
   * Place number pills on the game board
   * Called from App.tsx via callback
   */
  async placeNumberPills(
    answers: number[],
    correctAnswer: number
  ): Promise<boolean> {
    const success = await this.pillManager.placeNumberPills(
      answers,
      correctAnswer
    );
    // Mark pills as placed when React places them directly (e.g., after eating a pill)
    if (success) {
      this.pillManager.markPillsAsPlaced();
    }
    return success;
  }

  /**
   * Clear the pills placed flag in PillManager
   * Called from App.tsx when life is lost or game is reset
   */
  clearPillsPlacedFlag(): void {
    this.pillManager.clearPillsPlacedFlag();
  }

  /**
   * Handle Pacman's death sequence
   */
  pacmanDies(): void {
    if (!this.isPacmanAlive) return;

    this.lives -= 1;

    if (this.lives > 0) {
      this.setGameState(GameState.RESPAWN_DELAY);
    } else {
      this.setGameState(GameState.END_DELAY);
    }

    // Play death sound
    this.soundManager.playSound(SOUND_KEYS.KILLED_BY_GHOST);

    // Stop switch mode timer (cleanup handled by GhostAI)
    this.ghostAI.cleanup();

    // Switch background music to normal mode
    this.soundManager.playBackgroundMusic(SOUND_KEYS.GHOST_NORMAL_MOVE);

    this.pacman.setVelocityY(0);
    this.pacman.setVelocityX(0);
    this.isPacmanAlive = false;
    this.pacman.anims.stop();

    this.pacman.play(ANIMATION_KEYS.PACMAN_DEATH);
    this.time.delayedCall(TIMING.DEATH_ANIMATION_DURATION, () => {
      this.resetAfterDeath();
    });
  }

  /**
   * Reset the game state after Pacman dies
   */
  resetAfterDeath(): void {
    if (this.lives > 0) {
      // Clear PillManager's pills placed flag BEFORE setting state to PLAYING
      // This ensures placePillsIfNeeded() can place pills when state changes
      this.pillManager.clearPillsPlacedFlag();

      // Call onLifeLost function when a life is lost (clears React's flag too)
      if (this.onLifeLost) {
        this.onLifeLost(this.lives);
      }

      // Set state to PLAYING - this will trigger placePillsIfNeeded()
      // Play normal mode sound (coffee-break was stopped by setGameState)
      this.soundManager.playBackgroundMusic(SOUND_KEYS.GHOST_NORMAL_MOVE);
      this.setGameState(GameState.PLAYING);

      // Play new life sound
      this.soundManager.playSound(SOUND_KEYS.NEW_LIFE);

      // Reset Pacman position
      this.pacman.setPosition(PACMAN_START_POSITION.X, PACMAN_START_POSITION.Y);

      // Reset ghosts
      this.ghostAI.resetGhosts(this.ghosts);

      // Reset collision manager
      this.collisionManager.reset();

      // Recreate Pacman animation
      this.anims.create({
        key: ANIMATION_KEYS.PACMAN_ANIM,
        frames: [
          { key: "pacman1" },
          { key: "pacman2" },
          { key: "pacman3" },
          { key: "pacman4" },
        ],
        frameRate: ANIMATION_FRAME_RATES.PACMAN,
        repeat: -1,
      });
      this.pacman.play(ANIMATION_KEYS.PACMAN_ANIM);

      // Reset movement manager
      this.movementManager.reset(DIRECTIONS.RIGHT, DIRECTIONS.RIGHT);
      this.movementManager.setHasRespawned(true);
    } else {
      // Call onGameOver function when the game ends
      if (this.onGameOver) {
        this.onGameOver();
      }
      this.endGame();
    }

    this.isPacmanAlive = true;
  }

  /**
   * Respawn a ghost after being eaten
   */
  respawnGhost(ghost: Ghost): void {
    this.ghostAI.respawnGhost(ghost);
  }

  /**
   * Set the game state and notify listeners
   */
  private setGameState(newState: GameState): void {
    console.log("Setting game state to: ", newState);
    this.gameState = newState;

    // Handle background music based on game state
    if (newState === GameState.MENU || newState === GameState.GAME_OVER) {
      // Play coffee-break music for menu and game over screens
      this.soundManager.stopBackgroundMusic();
      this.soundManager.playBackgroundMusic(SOUND_KEYS.COFFEE_BREAK);
    } else if (newState === GameState.PLAYING) {
      // this.soundManager.stopBackgroundMusic();
      // Note: Ghost music is already started by startGame(), resumeGame(), or resetAfterDeath()
      // before setGameState(PLAYING) is called, so we don't stop music here

      // Automatically place pills when game enters PLAYING state
      console.log("Game state is PLAYING, calling placePillsIfNeeded...");
      this.pillManager.placePillsIfNeeded().catch((error) => {
        console.error("Error placing pills automatically:", error);
      });
    }

    if (this.onGameStateChange) {
      this.onGameStateChange(newState);
    }
  }

  /**
   * Start the game
   */
  async startGame(): Promise<boolean> {
    try {
      console.log("Starting game...");

      if (this.gameState === GameState.PLAYING) {
        console.warn("Game is already playing");
        return false;
      }

      this.setGameState(GameState.START_DELAY);

      if (this.setRunningGameState) {
        this.setRunningGameState(RunningGameState.DELAY_PHASE);
      }

      // Stop coffee-break music
      this.soundManager.stopBackgroundMusic();

      // Reset game to initial state
      this.lives = INITIAL_GAME_STATE.LIVES;
      this.isPacmanAlive = true;

      // Reset positions if game objects exist
      if (this.pacman) {
        this.pacman.setPosition(
          PACMAN_START_POSITION.X,
          PACMAN_START_POSITION.Y
        );
        this.pacman.setVelocity(0, 0);
        this.pacman.setVisible(true);
        this.pacman.setActive(true);

        // Reset Pacman animation (in case it was stuck in death animation)
        this.pacman.anims.stop();
        this.anims.create({
          key: ANIMATION_KEYS.PACMAN_ANIM,
          frames: [
            { key: ASSET_KEYS.PACMAN },
            { key: ASSET_KEYS.PACMAN1 },
            { key: ASSET_KEYS.PACMAN2 },
            { key: ASSET_KEYS.PACMAN3 },
            { key: ASSET_KEYS.PACMAN4 },
            { key: ASSET_KEYS.PACMAN5 },
          ],
          frameRate: ANIMATION_FRAME_RATES.PACMAN,
          repeat: -1,
        });
        this.pacman.play(ANIMATION_KEYS.PACMAN_ANIM);
      }

      // Reset ghosts if they exist
      if (this.ghosts && this.ghosts.length > 0) {
        this.ghostAI.resetGhostPositions(this.ghosts);
      }

      // Reset movement manager
      this.movementManager.reset(
        INITIAL_GAME_STATE.DIRECTION,
        INITIAL_GAME_STATE.PREVIOUS_DIRECTION
      );

      // Reset collision manager
      this.collisionManager.reset();

      // Play game start sound (one-time only)
      this.soundManager.playSound(SOUND_KEYS.START_MUSIC, { loop: false });

      // Set movement manager to active state
      this.movementManager.setIsActive(false);

      // Start ghost entries after a delay
      this.time.delayedCall(TIMING.GHOST_ENTRY_DELAY, () => {
        // Set movement manager to active state
        this.movementManager.setIsActive(true);

        if (this.setRunningGameState) {
          this.setRunningGameState(RunningGameState.RUNNING);
        }

        // Play normal mode sound
        this.soundManager.playBackgroundMusic(SOUND_KEYS.GHOST_NORMAL_MOVE);
        console.log("Background music started");

        // Start physics if paused
        if (this.physics.world.isPaused) {
          this.physics.resume();
          this.ghostAI.startGhostEntries();
        } else {
          // Reset ghosts to starting positions and states
          this.ghostAI.resetGhosts(this.ghosts);
        }

        // Initialize mode timers
        this.ghostAI.initModeTimers();

        // Start regeneration timer
        this.boardManager.startRegenerationTimer();

        // Set movement manager to respawned state
        this.movementManager.setHasRespawned(true);

        // Set game state to playing
        this.setGameState(GameState.PLAYING);
        console.log("Game started successfully");
      });

      return true;
    } catch (error) {
      console.error("Failed to start game:", error);
      return false;
    }
  }

  /**
   * End the game
   */
  async endGame(): Promise<boolean> {
    try {
      console.log("Ending game...");

      if (
        this.gameState === GameState.MENU ||
        this.gameState === GameState.GAME_OVER
      ) {
        console.warn("Game is not active");
        return false;
      }

      // Stop all movement but DON'T destroy sprites (so they can be reused)
      if (this.pacman && this.pacman.active) {
        this.pacman.setVelocity(0, 0);
        this.pacman.setPosition(
          PACMAN_START_POSITION.X,
          PACMAN_START_POSITION.Y
        );
        this.pacman.setVisible(true);
        this.pacman.setActive(true);
      }

      // Reset ghosts to starting positions
      if (this.ghosts && this.ghosts.length > 0) {
        this.ghostAI.resetGhostPositions(this.ghosts);
      }

      // Stop all sounds (coffee-break will be played by setGameState)
      this.soundManager.stopAllSounds();

      // Pause physics
      this.physics.pause();

      // Stop timers
      this.boardManager.stopRegenerationTimer();
      this.ghostAI.cleanup();

      // Set game state (this will play coffee-break music)
      this.setGameState(GameState.GAME_OVER);

      // Call game over callback
      if (this.onGameOver) {
        this.onGameOver();
      }

      console.log("Game ended successfully");
      return true;
    } catch (error) {
      console.error("Failed to end game:", error);
      return false;
    }
  }

  /**
   * Pause the game
   */
  async pauseGame(): Promise<boolean> {
    try {
      if (this.gameState !== GameState.PLAYING) {
        console.warn("Can only pause when game is playing");
        return false;
      }

      // Stop current background music and play coffee-break music
      this.soundManager.stopBackgroundMusic();
      this.soundManager.playBackgroundMusic(SOUND_KEYS.COFFEE_BREAK);

      // Pause physics
      this.physics.pause();

      // Pause Phaser's time system (pauses all time.delayedCall timers)
      if (!this.scene.isPaused()) {
        this.scene.pause();
      }

      // Stop timers but keep them for resume
      this.boardManager.stopRegenerationTimer();

      // Pause GhostAI timers (modeTimer, entryTimer, blinkInterval)
      this.ghostAI.pauseTimers();

      this.setGameState(GameState.PAUSED);
      console.log("Game paused successfully");
      return true;
    } catch (error) {
      console.error("Failed to pause game:", error);
      return false;
    }
  }

  /**
   * Resume the game
   */
  async resumeGame(): Promise<boolean> {
    try {
      if (this.gameState !== GameState.PAUSED) {
        console.warn("Can only resume when game is paused");
        return false;
      }

      // Stop coffee-break music and resume appropriate ghost music
      this.soundManager.stopBackgroundMusic();
      const currentMode = this.ghostAI?.getCurrentMode();
      if (currentMode === GAME_MODES.SCARED) {
        this.soundManager.playBackgroundMusic(SOUND_KEYS.GHOST_SCARED_MODE);
      } else {
        this.soundManager.playBackgroundMusic(SOUND_KEYS.GHOST_NORMAL_MOVE);
      }

      // Resume Phaser's time system (resumes all time.delayedCall timers)
      if (this.scene.isPaused()) {
        this.scene.resume();
      }

      // Resume physics
      this.physics.resume();

      // Restart timers
      this.boardManager.startRegenerationTimer();

      // Resume GhostAI timers (modeTimer, entryTimer, blinkInterval)
      this.ghostAI.resumeTimers();

      this.setGameState(GameState.PLAYING);
      console.log("Game resumed successfully");
      return true;
    } catch (error) {
      console.error("Failed to resume game:", error);
      return false;
    }
  }

  /**
   * Reset the game
   */
  async resetGame(): Promise<boolean> {
    try {
      console.log("Resetting game...");

      // Don't destroy sprites - just stop timers and reset state
      // Stop timers
      this.boardManager.stopRegenerationTimer();
      this.ghostAI.cleanup();

      // Stop all sounds
      this.soundManager.stopAllSounds();

      // Pause physics
      if (!this.physics.world.isPaused) {
        this.physics.pause();
      }

      // Reset Pacman if it exists
      if (this.pacman && this.pacman.active) {
        this.pacman.setPosition(
          PACMAN_START_POSITION.X,
          PACMAN_START_POSITION.Y
        );
        this.pacman.setVelocity(0, 0);
        this.pacman.setVisible(true);
        this.pacman.setActive(true);
      }

      // Reset ghosts
      if (this.ghosts && this.ghosts.length > 0) {
        this.ghostAI.resetGhostPositions(this.ghosts);
      }

      // Regenerate dots and power pills
      this.boardManager.regenerateDotsAndPowerPills();

      // Reset game state variables
      this.lives = INITIAL_GAME_STATE.LIVES;
      this.isPacmanAlive = true;

      // Reset collision manager
      this.collisionManager.reset();

      // Set back to menu state
      this.setGameState(GameState.MENU);

      console.log("Game reset successfully");
      return true;
    } catch (error) {
      console.error("Failed to reset game:", error);
      return false;
    }
  }

  /**
   * Get current game state
   */
  getCurrentState(): GameState {
    return this.gameState;
  }

  /**
   * Check if game is active
   */
  isGameActive(): boolean {
    return (
      this.gameState === GameState.PLAYING ||
      this.gameState === GameState.PAUSED
    );
  }

  // ========== Sound Management Delegation ==========

  toggleSoundMute(): boolean {
    const wasMuted = this.soundManager.isMutedState();
    const isNowMuted = this.soundManager.toggleMute();

    // If we just unmuted and game is playing, play the appropriate background music
    if (wasMuted && !isNowMuted && this.gameState === GameState.PLAYING) {
      const currentMode = this.ghostAI?.getCurrentMode();
      if (currentMode === GAME_MODES.SCARED) {
        this.soundManager.playBackgroundMusic(SOUND_KEYS.GHOST_SCARED_MODE);
      } else {
        this.soundManager.playBackgroundMusic(SOUND_KEYS.GHOST_NORMAL_MOVE);
      }
    }

    return isNowMuted;
  }

  setMasterVolume(volume: number): void {
    this.soundManager.setMasterVolume(volume);
  }

  setSfxVolume(volume: number): void {
    this.soundManager.setSfxVolume(volume);
  }

  setMusicVolume(volume: number): void {
    this.soundManager.setMusicVolume(volume);
  }

  isSoundMuted(): boolean {
    return this.soundManager.isMutedState();
  }

  getMasterVolume(): number {
    return this.soundManager.getMasterVolume();
  }

  getSfxVolume(): number {
    return this.soundManager.getSfxVolume();
  }

  getMusicVolume(): number {
    return this.soundManager.getMusicVolume();
  }

  // ========== Asset Loading ==========

  private loadGameAssets(): void {
    // Tilemap
    this.load.tilemapTiledJSON(ASSET_KEYS.MAP, ASSET_PATHS.MAP);
    this.load.image(ASSET_KEYS.TILESET, ASSET_PATHS.TILESET);

    const frameDimensions = {
      frameWidth: SPRITE_DIMENSIONS.FRAME_WIDTH,
      frameHeight: SPRITE_DIMENSIONS.FRAME_HEIGHT,
    };

    // Pacman sprites - load as spritesheets to enforce consistent frame dimensions
    // This removes any whitespace/padding differences between images
    this.load.spritesheet(
      ASSET_KEYS.PACMAN,
      ASSET_PATHS.PACMAN,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN1,
      ASSET_PATHS.PACMAN1,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN2,
      ASSET_PATHS.PACMAN2,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN3,
      ASSET_PATHS.PACMAN3,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN4,
      ASSET_PATHS.PACMAN4,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN5,
      ASSET_PATHS.PACMAN5,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN_DEATH1,
      ASSET_PATHS.PACMAN_DEATH1,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN_DEATH2,
      ASSET_PATHS.PACMAN_DEATH2,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN_DEATH3,
      ASSET_PATHS.PACMAN_DEATH3,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN_DEATH4,
      ASSET_PATHS.PACMAN_DEATH4,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN_DEATH5,
      ASSET_PATHS.PACMAN_DEATH5,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN_DEATH6,
      ASSET_PATHS.PACMAN_DEATH6,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN_DEATH7,
      ASSET_PATHS.PACMAN_DEATH7,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN_DEATH8,
      ASSET_PATHS.PACMAN_DEATH8,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN_DEATH9,
      ASSET_PATHS.PACMAN_DEATH9,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN_DEATH10,
      ASSET_PATHS.PACMAN_DEATH10,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN_DEATH11,
      ASSET_PATHS.PACMAN_DEATH11,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PACMAN_DEATH12,
      ASSET_PATHS.PACMAN_DEATH12,
      frameDimensions
    );

    // Ghost sprites - load as spritesheets for consistent dimensions
    this.load.spritesheet(
      ASSET_KEYS.BLUE_GHOST,
      ASSET_PATHS.BLUE_GHOST,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.BLUE_GHOST1,
      ASSET_PATHS.BLUE_GHOST1,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PINK_GHOST,
      ASSET_PATHS.PINK_GHOST,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.PINK_GHOST1,
      ASSET_PATHS.PINK_GHOST1,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.RED_GHOST,
      ASSET_PATHS.RED_GHOST,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.RED_GHOST1,
      ASSET_PATHS.RED_GHOST1,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.ORANGE_GHOST,
      ASSET_PATHS.ORANGE_GHOST,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.ORANGE_GHOST1,
      ASSET_PATHS.ORANGE_GHOST1,
      frameDimensions
    );

    this.load.spritesheet(
      ASSET_KEYS.AFRAID_GHOST,
      ASSET_PATHS.SCARED_GHOST,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.SCARED_GHOST,
      ASSET_PATHS.SCARED_GHOST,
      frameDimensions
    );
    this.load.spritesheet(
      ASSET_KEYS.SCARED_GHOST_WHITE,
      ASSET_PATHS.SCARED_GHOST_WHITE,
      frameDimensions
    );

    // Items
    this.load.image(ASSET_KEYS.DOT, ASSET_PATHS.DOT);
    this.load.image(ASSET_KEYS.POWER_PILL, ASSET_PATHS.POWER_PILL);
    this.load.image(ASSET_KEYS.LIFE_COUNTER, ASSET_PATHS.LIFE_COUNTER);
  }

  private loadSoundAssets(): void {
    // Load sound assets
    this.load.audio(SOUND_KEYS.START_MUSIC, "sounds/start-music.mp3");
    this.load.audio(SOUND_KEYS.COFFEE_BREAK, "sounds/coffee-break.mp3");
    this.load.audio(SOUND_KEYS.EATING_GHOST, "sounds/eating-the-ghost.mp3");
    this.load.audio(
      SOUND_KEYS.GHOST_NORMAL_MOVE,
      "sounds/ghost-normal-move.mp3"
    );
    this.load.audio(
      SOUND_KEYS.GHOST_SCARED_MODE,
      "sounds/ghost-scared-mode.mp3"
    );
    this.load.audio(
      SOUND_KEYS.GHOST_RETURN_HOME,
      "sounds/ghost-return-to-home.mp3"
    );
    this.load.audio(SOUND_KEYS.CREDIT_SOUND, "sounds/credit-sound.mp3");
    this.load.audio(SOUND_KEYS.KILLED_BY_GHOST, "sounds/killed-by-ghost.mp3");
    this.load.audio(SOUND_KEYS.NEW_LIFE, "sounds/new-life.mp3");
    this.load.audio(SOUND_KEYS.CORRECT_ANSWER, "sounds/correct-answer.wav");
  }

  /**
   * Scene shutdown cleanup
   * Called automatically by Phaser when the scene is shut down
   * Cleans up timers and prevents memory leaks
   */
  shutdown(): void {
    this.boardManager?.stopRegenerationTimer();
    if (this.modeTimer) {
      clearTimeout(this.modeTimer);
      this.modeTimer = null;
    }
    this.ghostAI?.cleanup();

    // Clean up sound manager
    if (this.soundManager) {
      this.soundManager.destroy();
    }
  }
}
